import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer den Dialog "Berechtigungen verwalten"
 * (`UserPermissionsDialogComponent`). Wird ueber den `<app-dialog-help-button>`
 * im Dialog-Header gestartet (Pattern analog `EMPLOYEE_ADD_HELP_TOUR`).
 *
 * Erklaerend (nextCondition 'manual', Spotlight + Voice-Over). Reisst die
 * Themen des komplexen Dialogs grob an, ohne alle 130+ Einzel-Berechtigungen
 * durchzugehen:
 *  1. Ueberblick: wessen Berechtigungen werden hier verwaltet
 *  2. Drei Bereiche (Berechtigungen / Datenzugriff / Audit-Verlauf)
 *  3. Kategorien-Sidebar (Operativ vs. System + x/y-Zaehler + Suche)
 *  4. Berechtigungen schalten (Ja/Nein, Eltern/Kind-Hierarchie)
 *  5. Sensible & kritische Rechte (Gehalt/Bank/Steuer; Token-Entzug)
 *  6. Vorlage anwenden (Rollen-Vorlage als Schnellstart)
 *  7. Speichern (Dirty-State, Zuruecksetzen, kritische Bestaetigung)
 *
 * `popoverOutsideDialog: true` haelt den Popover NEBEN dem grossen Dialog —
 * er verdeckt so nie den Bereich, den er erklaert. Der Dialog ist KEIN
 * mat-accordion → kein panelIndex noetig. Cues (Tabs + Gruppen) leben im
 * Voice-Over-Manifest unter `permissions-help`.
 */

/**
 * Stellt sicher, dass der Berechtigungen-Tab (erstes Segment) aktiv ist.
 * Seit der `xds-tabs`-Migration (2026-07-11) tragen die Tab-Buttons die
 * Kit-Klassen `.xds-tab`/`.is-active` statt der früheren `.pm-tab`/`.active`.
 */
function ensurePermissionsSegment(): void {
  const tab = document.querySelector<HTMLElement>('.pm-segment-tabs .xds-tab');
  if (tab && !tab.classList.contains('is-active')) {
    tab.click();
  }
}

/**
 * Aktiviert die erste Kategorie (Mitarbeiter) — sie zeigt die reichste
 * Familien-Hierarchie inkl. sensibler Zeilen (Gehalt/Bank/Steuer). Stellt
 * vorher den Berechtigungen-Tab sicher, damit die Sidebar ueberhaupt da ist.
 * Seit der `xds-nav-list`-Migration (2026-07-11) tragen die Sidebar-Items die
 * Kit-Klassen `.xds-nav-item`/`.is-active` statt der früheren `.pm-side-item`/`.active`.
 */
function activateFirstCategory(): void {
  ensurePermissionsSegment();
  const cat = document.querySelector<HTMLElement>('.pm-sidebar .xds-nav-item');
  if (cat && !cat.classList.contains('is-active')) {
    cat.click();
  }
}

const DIALOG_CTX = {
  attachToDialog: true,
  autoCloseOnDialogClose: true,
  popoverOutsideDialog: true,
} as const;

export const PERMISSIONS_HELP_TOUR: TourConfig = {
  id: TourFeature.PermissionsHelp,
  version: 1,
  title: 'PERMISSIONS_HELP_TOUR_TITLE',
  description: 'PERMISSIONS_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: nur zur Mitarbeiterliste navigieren. Der Permissions-
  // Dialog ist 2-stufig (Mitarbeiter-Edit oeffnen → "Berechtigungen verwalten"
  // im Aktionsmenue) — `launchContext.opens` adressiert nur eine Stufe.
  // Phase-1-Loesung: `route` only, ohne `opens` — User landet auf der Liste
  // und oeffnet selbst. Im Dialog laeuft die Tour wie heute via
  // `<app-dialog-help-button>`. Phase 2 koennte `opens: ChainStep[]`
  // einfuehren.
  launchContext: {
    route: '/employee',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 120,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'PERMISSIONS_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.PermissionsDialog,
          spotlightAnchor: AnchorId.PermissionsDialog,
          title: 'TUTORIAL.PERMISSIONS.WELCOME.TITLE',
          content: 'TUTORIAL.PERMISSIONS.WELCOME.CONTENT',
          placement: 'right',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'tabs',
          anchor: AnchorId.PermissionsTabs,
          spotlightAnchor: AnchorId.PermissionsTabs,
          title: 'TUTORIAL.PERMISSIONS.TABS.TITLE',
          content: 'TUTORIAL.PERMISSIONS.TABS.CONTENT',
          placement: 'right',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'sidebar',
          anchor: AnchorId.PermissionsSidebar,
          spotlightAnchor: AnchorId.PermissionsSidebar,
          title: 'TUTORIAL.PERMISSIONS.SIDEBAR.TITLE',
          content: 'TUTORIAL.PERMISSIONS.SIDEBAR.CONTENT',
          placement: 'right',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          onBeforeShow: () => ensurePermissionsSegment(),
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="permissions-sidebar"]'),
        },
        {
          id: 'table',
          anchor: AnchorId.PermissionsTable,
          spotlightAnchor: AnchorId.PermissionsTable,
          title: 'TUTORIAL.PERMISSIONS.TABLE.TITLE',
          content: 'TUTORIAL.PERMISSIONS.TABLE.CONTENT',
          placement: 'right',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          onBeforeShow: () => activateFirstCategory(),
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="permissions-table"]'),
        },
        {
          id: 'sensitive',
          anchor: AnchorId.PermissionsSensitive,
          spotlightAnchor: AnchorId.PermissionsSensitive,
          title: 'TUTORIAL.PERMISSIONS.SENSITIVE.TITLE',
          content: 'TUTORIAL.PERMISSIONS.SENSITIVE.CONTENT',
          placement: 'right',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          // Mitarbeiter-Kategorie aktivieren → sensible Zeilen (Gehalt/Bank/
          // Steuer) sind gerendert und der Anker existiert.
          onBeforeShow: () => activateFirstCategory(),
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="permissions-sensitive"]'),
        },
        {
          id: 'template',
          anchor: AnchorId.PermissionsTemplate,
          spotlightAnchor: AnchorId.PermissionsTemplate,
          title: 'TUTORIAL.PERMISSIONS.TEMPLATE.TITLE',
          content: 'TUTORIAL.PERMISSIONS.TEMPLATE.CONTENT',
          placement: 'right',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'save',
          anchor: AnchorId.PermissionsFooter,
          spotlightAnchor: AnchorId.PermissionsFooter,
          title: 'TUTORIAL.PERMISSIONS.SAVE.TITLE',
          content: 'TUTORIAL.PERMISSIONS.SAVE.CONTENT',
          placement: 'right',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
